import { CommonModule, DOCUMENT } from "@angular/common";
import { afterNextRender, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { filter, firstValueFrom, Subject, takeUntil } from "rxjs";
import { Client, NavbarItem } from "src/app/types";
import { AppPipes } from "../../pipes";
import { AuthService } from "../../services/auth/auth.service";
import { ClientService } from "../../services/client/client.service";
import { NotificationService } from "../../services/notification/notification.service";
import { QueryParamsService } from "../../services/query-params/query-params.service";
import { SharedService } from "../../services/shared-service/shared.service";
import { StorageService } from "../../services/storage/storage.service";
import { TranslateService } from "../../services/translation/translate.service";
import { WINDOW } from "../../services/window/window.service";
import { FlagComponent } from "../flag";
import { UserData, UserService } from "../../services/user/user.service";
import { IsEnabledDirective } from "../../directives/isEnabled.directive";

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.less'],
  imports: [
    CommonModule,
    RouterModule,
    AppPipes,
    FlagComponent,
    IsEnabledDirective,
  ],
})

export class NavBarComponent implements OnInit, OnDestroy{
  @ViewChild('secondaryMenuInstance', { static: false }) secondaryMenuInstance!: ElementRef<HTMLElement>;
  @ViewChild('mainMenuInstance', { static: false }) mainMenuInstance!: ElementRef<HTMLElement>;
  @ViewChild('secondaryMoreBtn', { static: false }) secondaryMoreBtn!: ElementRef<HTMLElement>;
  @ViewChild('mainMoreBtn', { static: false }) mainMoreBtn!: ElementRef<HTMLElement>;

  public clientObject!: Client;
  public mainMenu!: NavbarItem[];
  public mainMenuHiddenCount = 0;
  public mainMenuExtraVisible = false;
  public secondaryMenu!: NavbarItem[];
  public secondaryMenuHiddenCount = 0;
  public secondaryMenuExtraVisible = false;
  public language!: string;
  public selectedCurrency = this.Translate?.currency;
  public selectedLanguage = this.Translate?.language;
  public languageVisible = false;
  public currencyVisible = false;
  public isLoggedIn = false;
  private isLoggedInObservable = toObservable(this.AuthService.isLoggedIn);
  private authDataObservable = toObservable(this.AuthService.authData);
  private destroy$ = new Subject();
  public mobileMenuVisible = false;
  public mainMenuHeight!: number;
  private resizeDebounce: any;
  public user:UserData | undefined;
  public userMenuVisible = false;
  @HostListener('window:resize') listenResize() {
    if (this.SharedService.isBrowser()) {
      clearTimeout(this.resizeDebounce);
      this.resizeDebounce = setTimeout(() => {
        this.onResize();
      }, 120);
    }
  }

  constructor(
    @Inject(WINDOW) public Window: Window,
    @Inject(DOCUMENT) private document: Document,
    private Client: ClientService,
    public Router: Router,
    private Translate: TranslateService,
    private StorageService: StorageService,
    private AuthService: AuthService,
    private SharedService: SharedService,
    private NotificationService: NotificationService,
    private Host: ElementRef<HTMLElement>,
    private QueryParamsService: QueryParamsService,
    private cdr: ChangeDetectorRef,
    private User: UserService,
  ) {
    if (this.SharedService.isBrowser()) {
      this.watchAuthStatus();
    } else {
      afterNextRender(() => {
        this.watchAuthStatus();
      })
    }

    this.Router.events
    .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
    .subscribe((ev: NavigationEnd) => {
      this.resetUI();
    });

    this.SharedService.signOutListener.subscribe(() => {
      this.user = undefined;
    });
  }

  public navigateHome($event: MouseEvent): void {
    $event.preventDefault();
    const path = this.clientObject?.isFullWeb ? `${this.Translate.language}/home` : this.clientObject.homepageURL as string;

    if ($event.metaKey || $event.ctrlKey || $event.shiftKey) {
      this.Window.open(path, '_blank');
    }
    else if (this.clientObject?.isFullWeb) {
      this.Router.navigate([path]).then();
    } else {
      this.Window.location.href = path;
    }
  }
  
  closeDropdown(type:string) {
    if (type === 'currency') {
      this.currencyVisible = false
    } else if (type === 'language') {
      this.languageVisible = false
    } else if (type === 'user') {
      this.userMenuVisible = false
    }
  }

  useLanguage(lang: any) {
    const splitArray = this.Window.location.pathname.split('/');
    splitArray[1] = lang;
    this.StorageService.set('selectedLanguage', lang);
    this.Window.location.href = splitArray.join('/');
  }
  useCurrency(currency: string) {
    this.Translate.updateCurrentSelectedCurrency(currency);
  }

  watchAuthStatus() {
    this.isLoggedInObservable.pipe(
      takeUntil(this.destroy$),
    ).subscribe(async (val) => {
      this.isLoggedIn = this.AuthService.isLoggedIn();
      if (this.isLoggedIn) {
        this.getUserData();
      }
    });

    this.authDataObservable.pipe(
      takeUntil(this.destroy$),
    ).subscribe((val) => {
      this.isLoggedIn = this.AuthService.isLoggedIn();
      if (this.isLoggedIn) {
        this.getUserData();
      }
    });

    this.User.userDataListener.subscribe((response) => {
      this.user = response;
      setTimeout(() => {
        this.onResize();
      }, 0);
    });

  }

  private async getUserData() {
    await firstValueFrom(this.User.getUserData());
  }

  public signOut(): void {
    this.AuthService.signOut();
    this.NotificationService.showCustomMessage(
      '',
      'Successfully signed out.',
      'success-snackbar'
    );
    this.Router.navigate([`${this.selectedLanguage}/home`]);
  }

  private resetUI() {
    this.mobileMenuVisible = false;
    this.languageVisible = false;
    this.currencyVisible = false;
    this.mainMenuExtraVisible = false;
    this.secondaryMenuExtraVisible = false;
  }

  private onResize() {
    this.resetUI();
    const mainMenuItems = this.mainMenuInstance?.nativeElement?.querySelectorAll('.menuItem');
    const firstTop = mainMenuItems?.item(0)?.getBoundingClientRect()?.top;
    this.mainMenuHiddenCount = 0;
    let lastVisibleMain!: HTMLElement;
    if (typeof firstTop !== 'undefined') {   
      mainMenuItems.forEach((item, index) => {
        this.mainMenu[index]._visible = item.getBoundingClientRect().top === firstTop;
        if (!this.mainMenu[index]._visible) {
          this.mainMenuHiddenCount++;
        } else{
          lastVisibleMain = item as HTMLElement;
        }
      });
    }

    let lastVisibleSub!: HTMLElement;

    if (this.secondaryMenu?.length) {
      const secondaryMenuItems = this.secondaryMenuInstance?.nativeElement?.querySelectorAll('.menuItem');
      const secondaryTop = secondaryMenuItems?.item(0)?.getBoundingClientRect()?.top;
      this.secondaryMenuHiddenCount = 0;
      
      if (typeof secondaryTop !== 'undefined') {   
        secondaryMenuItems.forEach((item, index) => {
          this.secondaryMenu[index]._visible = item.getBoundingClientRect().top === secondaryTop;
          if (!this.secondaryMenu[index]._visible) {
            this.secondaryMenuHiddenCount++;
          } else{
            lastVisibleSub = item as HTMLElement;
          }
        });
      }

      
    }

    /* lets wait until everything is rendered */
    setTimeout(() => {
      /* position more button */
      if (lastVisibleMain && this.mainMoreBtn) {
        const mainLeft = this.mainMenuInstance.nativeElement.getBoundingClientRect().left;
        const lastLeft = lastVisibleMain.getBoundingClientRect().left;
        const lastWidth = lastVisibleMain.getBoundingClientRect().width;
        this.mainMoreBtn.nativeElement.style.left = `${lastLeft - mainLeft + lastWidth}px`;
      }
      if (lastVisibleSub && this.secondaryMoreBtn) {
        const subLeft = this.secondaryMenuInstance.nativeElement.getBoundingClientRect().left;
        const lastLeft = lastVisibleSub.getBoundingClientRect().left;
        const lastWidth = lastVisibleSub.getBoundingClientRect().width;
        this.secondaryMoreBtn.nativeElement.style.left = `${lastLeft - subLeft + lastWidth}px`;
      }
    }, 0);

    this.mainMenuHeight = this.Host.nativeElement.getBoundingClientRect().height;
    const root = this.document.documentElement;
    root.style.setProperty('--mainMenuHeight', `${this.mainMenuHeight}px`);
  }

  public scrollUp() {
    if (this.SharedService.isBrowser()) {
      this.Window.scrollTo(0, 0);
    }
    this.QueryParamsService.updateSharedNavigationChangedState(true);
  }


  private mapMenuItems() {
    if (this.clientObject?.clientName !== 'KB Auto') {
      return;
    }

    /* this is stupid */
    const kbMatch: Record<string, string> = {
      'about-us': 'aboutus',
      'start-selling': 'startselling',
    }

    const kbAuto = this.SharedService.kbAutoPageObject.find(i => i.lang === this.Translate.language)?.pages || [];
    this.mainMenu.forEach((item, index) => {
      if (item.isInternal) {
        const itemKey = item.link.replace(/^\//, '');
        const matchKey = kbMatch[itemKey] || itemKey;
        const kbLink = kbAuto.find(i => i.key === matchKey);
        if (kbLink) {
          this.mainMenu[index].link = kbLink.link;
          this.mainMenu[index].isFullLink = true;
        }
      }
      //const match = kbAuto.find(i => i.key === )
    });

  }
  
  private initialize() {
    this.clientObject = this.Client.client;
    this.mainMenu = structuredClone(this.clientObject?.listOfNavbarItems?.filter(i => i.headerOrderNumber)).sort((a, b) => {
      return a.headerOrderNumber - b.headerOrderNumber;
    });
    this.secondaryMenu = structuredClone(this.clientObject?.listOfNavbarItems?.filter(i => i.toolbarOrderNumber)).sort((a, b) => {
      return a.toolbarOrderNumber - b.toolbarOrderNumber;
    });

    this.mainMenu = this.mainMenu.map((i) => {
      if (i.filterString && i.filterString.length) {
        i.link = i.link.replace('/for-sale/', '/category/');
      }
      return i;
    });
    this.mapMenuItems();
    if (this.secondaryMenu?.length) {
      const root = this.document.documentElement;
      root.style.setProperty('--mainMenuHeight', `101px`);
    }

    this.language = this.Translate.language;

    setTimeout(() => {
      this.onResize();
    }, 100);

    this.selectedCurrency = this.Translate?.currency;
    this.selectedLanguage = this.Translate?.language;

    if (this.SharedService.isBrowser()) {
      if (!this.Client.client.listOfLanguages.includes(this.selectedLanguage)) {
        this.useLanguage(this.Client.client.defaultLanguage.toLowerCase());
      }
    }
    this.cdr.detectChanges();
  }

  public editMyProfile() {
    const token = this.AuthService.getRefreshToken() || '';
    const access_token = this.AuthService.getAccessToken() || '';
    const site = this.Window?.location?.origin;
    const id_token = this.AuthService.getIdToken() || '';
    this.AuthService.dashboardRedirect({token, site, id_token, access_token}).then((response:any) => {
      if (response?.redirectURL) {
        this.Window.location.href = response.redirectURL;
      }
    });
  }

  ngOnInit(): void {
    if (this.SharedService.isBrowser()) {
      this.initialize();
      this.Client.clientLoaded$.subscribe(() => {
        this.initialize();
      });
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}